import React, { useState, useEffect } from 'react';
import { EuiInMemoryTable, EuiLoadingSpinner } from '@elastic/eui';
import { OperationTableColumnDefinition } from '../Operation/OperationTableColumnDefinition';
import { convertEpochToDate } from '../../../utils';
import { pagination } from '../constants';
import { ErrorDisplay } from '../../../components/ErrorDisplay';
import ElementErrorBoundary from '../../../components/ErrorBoundary/ElementErrorBoundary';
import { useSdpdsReadOnlyOperation } from '../../../hooks/useSdpdsReadOnlyOperation';

export const OperationList = (props) => {
  const [operationDetailsList, setOperationDetailsList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [nextToken, setNextToken] = useState(null);

  const getPayload = (token = null) => ({
    stackName: props.stackName,
    clusterId: props.clusterId,
    maxResults: 50,
    ...(token && { nextToken: token })
  });

  const { refetch } = useSdpdsReadOnlyOperation({
    operation_name: "ListNodeOperationsForDomain",
    payload: getPayload()
  });

  useEffect(() => {
    refreshListOperations();
  }, [props.stackName, props.clusterId]);

  const refreshListOperations = async (token = null) => {
    setIsLoading(true);
    setIsError(false);

    try {
      const response = await fetchOperations(token);

      if (response.statusCode !== 200 || !response.body?.operationDetailsList) {
        setIsLoading(false);
        setIsError(true);
        return [];
      }

      const processedOperations = processOperations(response.body.operationDetailsList);
      if (response.body.nextToken) {
        const moreOperations = await refreshListOperations(response.body.nextToken);
        processedOperations.push(...moreOperations);
      }

      if (!token) {
        const sortedOperations = [...processedOperations].sort((a, b) => b.rawStartTime - a.rawStartTime);
        setOperationDetailsList(sortedOperations);
        setIsLoading(false);
        setIsError(false);
        setNextToken(response.body.nextToken);

        if (props.onOperationsUpdated) {
          props.onOperationsUpdated(sortedOperations);
        }
      }

      return processedOperations;
    } catch (error) {
      if (!token) {
        setIsLoading(false);
        setIsError(true);
        setOperationDetailsList([]);
      }
      return [];
    }
  };

  const fetchOperations = async (token) => {
    const result = await refetch({
      payload: getPayload(token)
    });

    if (result.data?.sdpdsReadOnlyOperation) {
      return {
        statusCode: 200,
        body: result.data.sdpdsReadOnlyOperation
      };
    }

    throw new Error('Failed to fetch operations');
  };

  const processOperations = (nodeOperations = []) => {
    const processed = nodeOperations.map(nodeOperation => ({
      ...nodeOperation,
      startTime: nodeOperation.startTime ? convertEpochToDate(nodeOperation.startTime) : OperationList.notApplicable,
      endTime: nodeOperation.endTime ? convertEpochToDate(nodeOperation.endTime) : OperationList.notApplicable,
      errorMessage: nodeOperation.errorMessage || OperationList.notApplicable,
      taskID: nodeOperation.taskID || OperationList.notApplicable,
      rawStartTime: Number(nodeOperation.startTime) || 0
    }));
    return [...processed].sort((a, b) => b.rawStartTime - a.rawStartTime);
  };

  const renderTable = () => {
    return (
      <EuiInMemoryTable
        pagination={pagination}
        columns={OperationTableColumnDefinition}
        items={operationDetailsList}
        sorting={{
          sort: {
            field: 'rawStartTime',
            direction: 'desc',
          },
        }}
        search={{
          box: {
            incremental: true,
            placeholder: 'Search nodeOperations...',
          },
        }}
      />
    );
  };

    if (isLoading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
          <EuiLoadingSpinner size="xl" />
        </div>
      );
    }

    if (isError) {
      return <ErrorDisplay />;
    }

    return (
      <ElementErrorBoundary>
        {renderTable()}
      </ElementErrorBoundary>
    );
  }

OperationList.notApplicable = 'N/A';